"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.clearBackup = exports.loadBackup = exports.saveBackup = exports.linesOf = exports.charCount = exports.splitBlock = exports.fromJstInput = exports.toJstInput = exports.move = exports.readFileAsBase64 = exports.blocksFromPaste = exports.pointLinesFromPaste = exports.tickerFromFilename = exports.convertBlock = exports.emptyBlock = exports.withoutIds = exports.withIds = exports.blockId = void 0;
let nextId = 0;
const blockId = () => `b${(nextId += 1)}`;
exports.blockId = blockId;
const withIds = (items) => items.map((item) => ({ ...item, id: (0, exports.blockId)() }));
exports.withIds = withIds;
const withoutIds = (blocks) => blocks.map(({ id: _id, ...item }) => item);
exports.withoutIds = withoutIds;
const emptyBlock = (type) => {
    switch (type) {
        case 'Header':
            return { id: (0, exports.blockId)(), type, level: 'h3', text: '' };
        case 'Img':
            return { id: (0, exports.blockId)(), type, src: '', width: 0, height: 0, alt: '' };
        default:
            return { id: (0, exports.blockId)(), type, text: '' };
    }
};
exports.emptyBlock = emptyBlock;
// Turns a block into another type, keeping its text where both have one.
const convertBlock = (block, type) => {
    if (block.type === type)
        return block;
    const text = block.type === 'Img' ? block.alt : block.text;
    if (type === 'Img')
        return { id: block.id, type, src: '', width: 0, height: 0, alt: text };
    if (type === 'Header')
        return { id: block.id, type, level: 'h3', text };
    return { id: block.id, type, text };
};
exports.convertBlock = convertBlock;
// Lines that read as a heading when pasted from Word or a memo: ■ 業績, ● 見通し,
// 【事業の状況】, # 見出し (Markdown).
const HEADING_LINE = /^\s*(?:[■●◆]|【.+】\s*$|#{1,4}\s)/;
const headingText = (line) => line
    .trim()
    .replace(/^#{1,4}\s+/, '')
    .replace(/^[■●◆]\s*/, '')
    .replace(/^【(.+)】$/, '$1')
    .trim();
const HEADING_LEVEL_OF = { 1: 'h2', 2: 'h3', 3: 'h4', 4: 'h4' };
// A Word file named after the company, 「8889_APAMAN.docx」「367a-決算.docx」: the
// four-character ticker at the start (digit, digit/letter, digit, digit/letter),
// not followed by another letter or digit. Null when the name does not start so.
const tickerFromFilename = (filename) => {
    // A date such as 「2026-11-13」 at the start is not a ticker.
    const match = /^(\d[0-9A-Z]\d[0-9A-Z])(?![0-9A-Z])(?![-/._]\d{1,2}[-/._]\d{1,2})/i.exec(filename.normalize('NFKC').trim());
    return match ? match[1].toUpperCase() : null;
};
exports.tickerFromFilename = tickerFromFilename;
// A list pasted into キーポイント: one line each, blank lines dropped, without the
// bullet or number in front (・ ● ■ - * • 1. ① …); Excel cells joined by a space.
const pointLinesFromPaste = (text) => text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line
    .split('\t')
    .map((cell) => cell.trim())
    .filter(Boolean)
    .join(' ')
    // 「-5%」「1.5倍」 keep their sign and number: - and * only with a space after, a number only before a non-digit.
    .replace(/^(?:[・●○■□◆◇▪▫•]\s*|[-*＊－]\s+|\(?\d{1,2}[.)．）](?!\d)\s*|[①-⑳]\s*)/u, '')
    .trim())
    .filter(Boolean);
exports.pointLinesFromPaste = pointLinesFromPaste;
// Pasted text with several lines becomes several blocks: one paragraph per
// line (blank lines dropped), heading-looking lines as 見出し. Rows copied from
// Excel (tab-separated) keep their cells apart with「｜」, as Word tables do.
const blocksFromPaste = (text) => text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => (line.includes('\t')
    ? line
        .split('\t')
        .map((cell) => cell.trim())
        .join('｜')
    : line).trim())
    .filter((line) => line !== '' && line.replace(/｜/g, '') !== '')
    .map((line) => {
    if (line.includes('｜') || !HEADING_LINE.test(line))
        return { id: (0, exports.blockId)(), type: 'P', text: line };
    const hashes = /^(#{1,4})\s/.exec(line)?.[1].length ?? 2;
    return { id: (0, exports.blockId)(), type: 'Header', level: HEADING_LEVEL_OF[hashes], text: headingText(line) };
});
exports.blocksFromPaste = blocksFromPaste;
const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
});
exports.readFileAsBase64 = readFileAsBase64;
const move = (list, from, to) => {
    if (to < 0 || to >= list.length)
        return list;
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};
exports.move = move;
// datetime-local value in JST (the input has no zone; the app is JST-only).
const JST_MS = 9 * 60 * 60 * 1000;
const toJstInput = (date) => new Date(new Date(date).getTime() + JST_MS).toISOString().slice(0, 16);
exports.toJstInput = toJstInput;
const fromJstInput = (value) => new Date(`${value}:00+09:00`).toISOString();
exports.fromJstInput = fromJstInput;
// Enter in the middle of a block: the text before the caret stays, the rest
// becomes a new paragraph after it (a heading or note is followed by a paragraph).
const splitBlock = (block, caret) => {
    if (block.type === 'Img')
        return [block, (0, exports.emptyBlock)('P')];
    const before = block.text.slice(0, caret).replace(/\s+$/, '');
    const after = block.text.slice(caret).replace(/^\s+/, '');
    return [
        { ...block, text: before },
        { id: (0, exports.blockId)(), type: 'P', text: after },
    ];
};
exports.splitBlock = splitBlock;
// Characters as an analyst counts them (no spaces or line breaks).
const charCount = (blocks) => blocks.reduce((n, block) => n + (block.type === 'Img' ? 0 : Array.from((block.text ?? '').replace(/\s/g, '')).length), 0);
exports.charCount = charCount;
const linesOf = (texts) => texts.map((text) => ({ id: (0, exports.blockId)(), text }));
exports.linesOf = linesOf;
const backupKey = (key) => `yahooArticleBackup:${key}`;
const saveBackup = (key, backup) => {
    try {
        localStorage.setItem(backupKey(key), JSON.stringify(backup));
    }
    catch {
        // Storage full or blocked: the article still saves to the server as usual.
    }
};
exports.saveBackup = saveBackup;
const loadBackup = (key) => {
    try {
        const raw = localStorage.getItem(backupKey(key));
        return raw ? JSON.parse(raw) : null;
    }
    catch {
        return null;
    }
};
exports.loadBackup = loadBackup;
const clearBackup = (key) => {
    try {
        localStorage.removeItem(backupKey(key));
    }
    catch {
        // ignore
    }
};
exports.clearBackup = clearBackup;
